import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import {
  OverpaymentDetails,
  OverpaymentDetailsSummary,
} from '../../../../models/api/view/claim/financials/overpayments/overpayment-details';
import { FormatClaimPipe } from '../../../../pipes/format-claim.pipe';
import { FormatPipe } from '../../../../pipes/format.pipe';
import { BaseComponent } from '../../../base/base.component';
import { BackButtonComponent } from '../../../core/buttons/back-button.component';
import { WatchButtonComponent } from '../../../core/buttons/watch-button.component';
import {
  ChartComponent,
  ChartConfig,
} from '../../../core/chart/chart.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { CardComponent } from '../../../core/panels/card.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-overpayment-detail',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    PageHeaderComponent,
    ActionRowComponent,
    BackButtonComponent,
    ChartComponent,
    CommonModule,
    CardComponent,
    WatchButtonComponent,
    FormatClaimPipe,
    FormatPipe,
  ],
  template: `
    <div class="tw-pb-2">
      <app-action-row>
        <app-back-button
          left1
          (click)="view.navigateCurrentClaim(['overpayments'])"
          >Back to overpayments</app-back-button
        >
      </app-action-row>
    </div>

    <app-page-header label="Overpayment details" />

    <div class="tw-flex tw-gap-8 tw-pb-8">
      <div class="tw-grow">
        <app-table
          [dataSource]="overpaymentDetails"
          [pageSize]="5"
          [scrollable]="false"
          dataKey="id"
          recordLabel="Overpayment detail"
          [sortField]="'action'"
          [showFilter]="false"
          [showFilterButton]="false"
          (filteredData)="onFilteredData($event)"
        >
          <app-column name="action" label="action" filterType="select" />
          <app-column
            name="actionDate"
            label="Action Date"
            format="d"
            filterType="date"
          />
          <app-column
            name="amount"
            label="amount"
            format="c"
            filterType="numeric"
          />
          <app-column name="actionReason" label="reason" filterType="select" />
          <app-column name="detailFileNum" label="claim" filterType="select">
            <ng-template let-data>
              {{ data.detailFileNum | formatClaim: claim.fileNumFormat }}
              <app-watch-button
                [recordSource]="claim.recordSource"
                [recordId]="claim.recordId"
              />
            </ng-template>
          </app-column>
          <app-column name="status" label="status" filterType="select" />
        </app-table>
      </div>
    </div>

    <div class="tw-flex tw-gap-4">
      <ng-container
        *ngTemplateOutlet="
          chart;
          context: {
            data: {
              label: 'Action summary',
              series: actionSeries,
              config: actionConfig,
            },
          }
        "
      ></ng-container>

      <ng-container
        *ngTemplateOutlet="
          chart;
          context: {
            data: {
              label: 'reason summary',
              series: reasonSeries,
              config: reasonConfig,
            },
          }
        "
      ></ng-container>

      <ng-container
        *ngTemplateOutlet="
          chart;
          context: {
            data: {
              label: 'year summary',
              series: yearSeries,
              config: yearConfig,
            },
          }
        "
      ></ng-container>

      <ng-container
        *ngTemplateOutlet="
          chart;
          context: {
            data: {
              label: 'status summary',
              series: statusSeries,
              config: statusConfig,
            },
          }
        "
      ></ng-container>
    </div>

    <ng-template #chart let-data="data">
      <div class="tw-basis-1/4 tw-flex tw-flex-col tw-gap-2">
        <app-card [label]="data.label">
          <app-chart [config]="data.config" />
          <div class="tw-flex tw-flex-col tw-gap-3 tw-items-center tw-py-4">
            @for (s of data.series; track s.label) {
              <div class="tw-w-full tw-flex tw-items-center">
                <div
                  class=" tw-size-6 tw-rounded-full tw-mr-2"
                  [style.background-color]="s.color"
                ></div>
                <div class="tw-font-bold  tw-text-[var(--sed-black-60-color)]">
                  {{ s.label }}
                </div>
                <div class="tw-grow"></div>
                <div class="tw-font-bold ">
                  {{ s.value | format: 'c' }}
                </div>
              </div>
            }
          </div>
        </app-card>
      </div>
    </ng-template>
  `,
  styles: ``,
})
export class OverpaymentDetailComponent
  extends BaseComponent
  implements OnInit
{
  private route = inject(ActivatedRoute);

  claim = this.view.currentClaim();

  actionConfig: ChartConfig;
  yearConfig: ChartConfig;
  reasonConfig: ChartConfig;
  statusConfig: ChartConfig;

  overpaymentDetails: OverpaymentDetails[] = [];

  colors = [
    '--sed-data-point-01-color',
    '--sed-data-point-02-color',
    '--sed-data-point-03-color',
    '--sed-data-point-04-color',
    '--sed-data-point-05-color',
    '--sed-data-point-06-color',
  ];

  actionSeries: any[] = [];
  yearSeries: any[] = [];
  reasonSeries: any[] = [];
  statusSeries: any[] = [];

  async ngOnInit() {
    const masterGroupIDStr = this.route.snapshot.paramMap.get('overpaymentId');
    const masterGroupID = masterGroupIDStr
      ? Number(masterGroupIDStr)
      : undefined;
    const claimKey: ClaimKey = {
      recordId: this.claim.recordId,
      recordSource: this.claim.recordSource,
    };
    const overpaymentsResponse = await this.api.overpayments.getDetails(
      claimKey,
      masterGroupID,
    );
    this.overpaymentDetails = overpaymentsResponse.map((o) => ({
      ...o,
      actionDate: o.actionDate ? new Date(o.actionDate) : null,
    }));
    this.loadOverpaymentsSummary(this.overpaymentDetails);
    this.view.hideLoading();
  }

  loadOverpaymentsSummary(overpayments: OverpaymentDetails[]) {
    const actionSummary = overpayments.reduce(
      (acc, x) => {
        acc[x.action] = (acc[x.action] || 0) + x.amount;
        return acc;
      },
      {} as Record<string, number>,
    );

    const reasonSummary = overpayments.reduce(
      (acc, x) => {
        acc[x.actionReason] = (acc[x.actionReason] || 0) + x.amount;
        return acc;
      },
      {} as Record<string, number>,
    );

    const yearSummary = overpayments.reduce(
      (acc, x) => {
        const year = new Date(x.actionDate).getFullYear().toString();
        acc[year] = (acc[year] || 0) + x.amount;
        return acc;
      },
      {} as Record<string, number>,
    );

    const statusSummary = overpayments.reduce(
      (acc, x) => {
        acc[x.status] = (acc[x.status] || 0) + x.amount;
        return acc;
      },
      {} as Record<string, number>,
    );

    const actionSummaryArray = this.mapToSummaryArray(actionSummary);
    const reasonSummaryArray = this.mapToSummaryArray(reasonSummary);
    const yearSummaryArray = this.mapToSummaryArray(yearSummary);
    const statusSummaryArray = this.mapToSummaryArray(statusSummary);

    this.actionConfig = new ChartConfig();
    this.loadCharts(this.actionConfig, actionSummaryArray);
    this.actionSeries = [...this.actionConfig.series];

    this.yearConfig = new ChartConfig();
    this.loadCharts(this.yearConfig, yearSummaryArray);
    this.yearSeries = [...this.yearConfig.series];

    this.reasonConfig = new ChartConfig();
    this.loadCharts(this.reasonConfig, reasonSummaryArray);
    this.reasonSeries = [...this.reasonConfig.series];

    this.statusConfig = new ChartConfig();
    this.loadCharts(this.statusConfig, statusSummaryArray);
    this.statusSeries = [...this.statusConfig.series];

    // this.cdr.markForCheck();
  }

  onFilteredData(event: any) {
    // Handle filtered data event
    this.loadOverpaymentsSummary(event);
  }

  loadCharts(chartConfig: ChartConfig, series: OverpaymentDetailsSummary[]) {
    chartConfig.showLabels = false;
    chartConfig.height = '100px';
    const sortedSeries = [...series].sort((a, b) =>
      a.action.localeCompare(b.action),
    );
    let i = 0;
    for (const s of sortedSeries) {
      const color = getComputedStyle(document.documentElement)
        .getPropertyValue(this.colors[i % this.colors.length])
        .trim();
      chartConfig.addSeries(s.action, s.totalAmount, color);
      i++;
    }
  }

  mapToSummaryArray(
    summary: Record<string, number>,
  ): OverpaymentDetailsSummary[] {
    return Object.entries(summary).map(
      ([key, value]) =>
        ({
          action: key,
          totalAmount: value as number,
        }) as OverpaymentDetailsSummary,
    );
  }
}
